/**
 * CustomControlsTab - tab "Controles personnalises" du Compliance Manager.
 * v2026.06.120
 *
 * Permet a l auditeur d ajouter ses propres controles (hors catalogue NIS365)
 * pour couvrir des exigences specifiques au client (politique interne, NDA
 * sectoriels, controles regulateur local, etc).
 *
 * Persistance : localStorage cle `nis365-custom-controls-{tenantId}` (mock).
 * Migration future : creer un container Cosmos `customControls` partitionne
 * par /tenantId, exposer 4 endpoints (list/create/update/delete).
 *
 * Modele :
 *   {
 *     id, title, condition, framework?, status, evidence?, createdAt, updatedAt
 *   }
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, Title3, Body1, Caption1, Button, Badge, Input, Textarea, Field,
  Dropdown, Option,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell,
  MessageBar, MessageBarBody,
  tokens,
} from '@fluentui/react-components';
import {
  Add20Regular, Edit20Regular, Delete20Regular, Attach20Regular,
  CheckmarkCircle20Filled, DismissCircle20Filled, Circle20Regular,
} from '@fluentui/react-icons';

// =============================================================================
//  TYPES
// =============================================================================

export type CustomControlStatus = 'pending' | 'conforme' | 'fail';

export type CustomFramework =
  | 'nis2' | 'iso27001' | 'cyfun' | 'cis-m365' | 'rgpd' | 'sectoriel' | 'autre';

export interface CustomControlEvidence {
  text?:        string;
  attachment?: {
    name:    string;
    type:    string;        // MIME
    size:    number;        // bytes raw
    dataUrl: string;        // "data:image/png;base64,..."
  };
}

export interface CustomControl {
  id:         string;
  title:      string;
  condition:  string;          // "Ce qui doit etre vrai pour etre conforme"
  framework?: CustomFramework;
  reference?: string;          // ex: "NIS2 Art.21(2)(d)"
  status:     CustomControlStatus;
  evidence?:  CustomControlEvidence;
  createdAt:  string;
  updatedAt:  string;
}

// =============================================================================
//  STORAGE (localStorage mock)
// =============================================================================

const STORAGE_PREFIX = 'nis365-custom-controls-';

function loadFromStorage(tenantId: string): CustomControl[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + tenantId);
    if (!raw) return [];
    const arr = JSON.parse(raw) as CustomControl[];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function saveToStorage(tenantId: string, list: CustomControl[]): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + tenantId, JSON.stringify(list));
  } catch { /* quota / privacy mode */ }
}

function newId(): string {
  return 'CUST-' + Math.random().toString(36).slice(2, 8).toUpperCase();
}

// =============================================================================
//  ATTACHMENT HELPER
// =============================================================================

const ATTACHMENT_MAX_BYTES = 500 * 1024; // 500 Ko

async function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('FileReader error'));
    reader.readAsDataURL(file);
  });
}

// =============================================================================
//  STATUS UI HELPERS
// =============================================================================

function StatusIcon({ status }: { status: CustomControlStatus }) {
  if (status === 'conforme') return <CheckmarkCircle20Filled primaryFill={tokens.colorPaletteGreenForeground1} />;
  if (status === 'fail')      return <DismissCircle20Filled  primaryFill={tokens.colorPaletteRedForeground1} />;
  return <Circle20Regular primaryFill={tokens.colorNeutralForeground3} />;
}

function StatusLabel({ status }: { status: CustomControlStatus }) {
  const { t } = useTranslation();
  const map: Record<CustomControlStatus, { color: string; bg: string; key: string }> = {
    pending:  { color: tokens.colorNeutralForeground3,        bg: tokens.colorNeutralBackground3, key: 'customControls.status.pending' },
    conforme: { color: tokens.colorPaletteGreenForeground1,   bg: '#E6F4EC', key: 'customControls.status.conforme' },
    fail:     { color: tokens.colorPaletteRedForeground1,     bg: '#FBE9E9', key: 'customControls.status.fail' },
  };
  const s = map[status];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '2px 10px', borderRadius: 12,
      background: s.bg, color: s.color, fontSize: 12, fontWeight: 600,
    }}>
      <StatusIcon status={status} />
      {t(s.key)}
    </span>
  );
}

// =============================================================================
//  MAIN COMPONENT
// =============================================================================

interface Props {
  tenantId: string;
  canEdit:  boolean;
}

export function CustomControlsTab({ tenantId, canEdit }: Props) {
  const { t } = useTranslation();
  const [list, setList]       = useState<CustomControl[]>([]);
  const [editing, setEditing] = useState<CustomControl | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  // Form state (edit dialog)
  const [fTitle, setFTitle]         = useState('');
  const [fCondition, setFCondition] = useState('');
  const [fFramework, setFFramework] = useState<CustomFramework | ''>('');
  const [fReference, setFReference] = useState('');
  const [fStatus, setFStatus]       = useState<CustomControlStatus>('pending');
  const [fEvidenceText, setFEvidenceText] = useState('');
  const [fAttachment, setFAttachment] = useState<CustomControlEvidence['attachment']>(undefined);

  // Load from storage on mount + tenant change
  useEffect(() => { setList(loadFromStorage(tenantId)); }, [tenantId]);

  // Stats
  const stats = useMemo(() => {
    const conforme = list.filter(c => c.status === 'conforme').length;
    const fail     = list.filter(c => c.status === 'fail').length;
    const pending  = list.filter(c => c.status === 'pending').length;
    return { total: list.length, conforme, fail, pending };
  }, [list]);

  function openCreate() {
    setEditing(null);
    setCreating(true);
    setFTitle(''); setFCondition('');
    setFFramework(''); setFReference('');
    setFStatus('pending');
    setFEvidenceText(''); setFAttachment(undefined);
    setError(null);
  }

  function openEdit(c: CustomControl) {
    setEditing(c); setCreating(false);
    setFTitle(c.title); setFCondition(c.condition);
    setFFramework(c.framework ?? '');
    setFReference(c.reference ?? '');
    setFStatus(c.status);
    setFEvidenceText(c.evidence?.text ?? '');
    setFAttachment(c.evidence?.attachment);
    setError(null);
  }

  function closeDialog() {
    setEditing(null); setCreating(false); setError(null);
  }

  async function onAttachmentChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > ATTACHMENT_MAX_BYTES) {
      setError(t('customControls.errors.attachmentTooLarge', { maxKB: ATTACHMENT_MAX_BYTES / 1024 }));
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setFAttachment({ name: file.name, type: file.type, size: file.size, dataUrl });
      setError(null);
    } catch {
      setError(t('customControls.errors.attachmentRead'));
    }
  }

  function save() {
    if (!fTitle.trim() || !fCondition.trim()) {
      setError(t('customControls.errors.titleConditionRequired'));
      return;
    }
    const now = new Date().toISOString();
    const evidence: CustomControlEvidence | undefined =
      (fEvidenceText.trim() || fAttachment)
        ? { text: fEvidenceText.trim() || undefined, attachment: fAttachment }
        : undefined;

    let next: CustomControl[];
    if (creating) {
      const c: CustomControl = {
        id:         newId(),
        title:      fTitle.trim(),
        condition:  fCondition.trim(),
        framework:  (fFramework || undefined) as CustomFramework | undefined,
        reference:  fReference.trim() || undefined,
        status:     fStatus,
        evidence,
        createdAt:  now,
        updatedAt:  now,
      };
      next = [c, ...list];
    } else if (editing) {
      next = list.map(x => x.id === editing.id ? {
        ...x,
        title:     fTitle.trim(),
        condition: fCondition.trim(),
        framework: (fFramework || undefined) as CustomFramework | undefined,
        reference: fReference.trim() || undefined,
        status:    fStatus,
        evidence,
        updatedAt: now,
      } : x);
    } else {
      return;
    }
    setList(next);
    saveToStorage(tenantId, next);
    closeDialog();
  }

  function quickStatus(c: CustomControl, status: CustomControlStatus) {
    const next = list.map(x => x.id === c.id
      ? { ...x, status, updatedAt: new Date().toISOString() }
      : x);
    setList(next);
    saveToStorage(tenantId, next);
  }

  function remove(c: CustomControl) {
    if (!window.confirm(t('customControls.confirmDelete', { id: c.id }))) return;
    const next = list.filter(x => x.id !== c.id);
    setList(next);
    saveToStorage(tenantId, next);
  }

  return (
    <div>
      {/* Header explicatif */}
      <Card style={{ padding: 16, marginBottom: 16, background: '#F4F8FA' }}>
        <Title3 block style={{ marginBottom: 4 }}>{t('customControls.title')}</Title3>
        <Body1 block style={{ color: tokens.colorNeutralForeground2 }}>
          {t('customControls.subtitle')}
        </Body1>
        <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
          <Caption1>
            <strong style={{ color: tokens.colorBrandForeground1 }}>{stats.total}</strong> {t('customControls.stats.total')}
          </Caption1>
          <Caption1>
            <strong style={{ color: tokens.colorPaletteGreenForeground1 }}>{stats.conforme}</strong> {t('customControls.stats.conforme')}
          </Caption1>
          <Caption1>
            <strong style={{ color: tokens.colorPaletteRedForeground1 }}>{stats.fail}</strong> {t('customControls.stats.fail')}
          </Caption1>
          <Caption1>
            <strong style={{ color: tokens.colorNeutralForeground3 }}>{stats.pending}</strong> {t('customControls.stats.pending')}
          </Caption1>
        </div>
        {canEdit && (
          <Button appearance="primary" icon={<Add20Regular />} onClick={openCreate} style={{ marginTop: 12 }}>
            {t('customControls.cta.create')}
          </Button>
        )}
      </Card>

      {/* Liste */}
      {list.length === 0 ? (
        <Card style={{ padding: 32, textAlign: 'center' }}>
          <Body1 block style={{ color: tokens.colorNeutralForeground3 }}>
            {t('customControls.empty')}
          </Body1>
        </Card>
      ) : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: 110 }}>{t('customControls.cols.id')}</TableHeaderCell>
                <TableHeaderCell>{t('customControls.cols.title')}</TableHeaderCell>
                <TableHeaderCell style={{ width: 160 }}>{t('customControls.cols.framework')}</TableHeaderCell>
                <TableHeaderCell style={{ width: 140 }}>{t('customControls.cols.status')}</TableHeaderCell>
                <TableHeaderCell style={{ width: 260, textAlign: 'right' }}>{t('customControls.cols.actions')}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <code style={{ fontSize: 12, fontFamily: 'monospace' }}>{c.id}</code>
                  </TableCell>
                  <TableCell>
                    <div>
                      <Body1 style={{ fontWeight: 600 }}>{c.title}</Body1>
                      <Caption1 block style={{ color: tokens.colorNeutralForeground3, marginTop: 2 }}>
                        {c.condition.length > 100 ? c.condition.slice(0, 100) + '...' : c.condition}
                      </Caption1>
                    </div>
                  </TableCell>
                  <TableCell>
                    {c.framework ? (
                      <Badge appearance="outline" size="small">
                        {c.framework.toUpperCase()}{c.reference ? ` - ${c.reference}` : ''}
                      </Badge>
                    ) : (
                      <Caption1 style={{ color: tokens.colorNeutralForeground4 }}>—</Caption1>
                    )}
                  </TableCell>
                  <TableCell><StatusLabel status={c.status} /></TableCell>
                  <TableCell style={{ textAlign: 'right' }}>
                    {canEdit && (
                      <div style={{ display: 'inline-flex', gap: 4 }}>
                        <Button
                          size="small"
                          appearance="subtle"
                          title={t('customControls.actions.markConforme')}
                          icon={<CheckmarkCircle20Filled primaryFill={tokens.colorPaletteGreenForeground1} />}
                          onClick={() => quickStatus(c, 'conforme')}
                        />
                        <Button
                          size="small"
                          appearance="subtle"
                          title={t('customControls.actions.markFail')}
                          icon={<DismissCircle20Filled primaryFill={tokens.colorPaletteRedForeground1} />}
                          onClick={() => quickStatus(c, 'fail')}
                        />
                        <Button
                          size="small"
                          appearance="subtle"
                          title={t('customControls.actions.edit')}
                          icon={<Edit20Regular />}
                          onClick={() => openEdit(c)}
                        />
                        <Button
                          size="small"
                          appearance="subtle"
                          title={t('customControls.actions.delete')}
                          icon={<Delete20Regular />}
                          onClick={() => remove(c)}
                        />
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Dialog create/edit */}
      {(creating || editing) && (
        <Dialog open={true} onOpenChange={(_, d) => { if (!d.open) closeDialog(); }}>
          <DialogSurface style={{ maxWidth: 700 }}>
            <DialogBody>
              <DialogTitle>
                {creating ? t('customControls.dialog.titleCreate') : t('customControls.dialog.titleEdit', { id: editing?.id })}
              </DialogTitle>
              <DialogContent>
                {error && (
                  <MessageBar intent="error" style={{ marginBottom: 12 }}>
                    <MessageBarBody>{error}</MessageBarBody>
                  </MessageBar>
                )}

                <Field label={t('customControls.fields.title')} required style={{ marginBottom: 12 }}>
                  <Input
                    value={fTitle}
                    onChange={(_, d) => setFTitle(d.value)}
                    placeholder={t('customControls.fields.titlePlaceholder')}
                  />
                </Field>

                <Field
                  label={t('customControls.fields.condition')}
                  required
                  hint={t('customControls.fields.conditionHint')}
                  style={{ marginBottom: 12 }}
                >
                  <Textarea
                    rows={3}
                    value={fCondition}
                    onChange={(_, d) => setFCondition(d.value)}
                    placeholder={t('customControls.fields.conditionPlaceholder')}
                  />
                </Field>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <Field label={t('customControls.fields.framework')}>
                    <Dropdown
                      value={fFramework ? t(`customControls.frameworks.${fFramework}`) : t('customControls.frameworks.none')}
                      selectedOptions={fFramework ? [fFramework] : []}
                      onOptionSelect={(_, d) => setFFramework((d.optionValue ?? '') as CustomFramework | '')}
                    >
                      <Option value="">{t('customControls.frameworks.none')}</Option>
                      <Option value="nis2">{t('customControls.frameworks.nis2')}</Option>
                      <Option value="iso27001">{t('customControls.frameworks.iso27001')}</Option>
                      <Option value="cyfun">{t('customControls.frameworks.cyfun')}</Option>
                      <Option value="cis-m365">{t('customControls.frameworks.cis-m365')}</Option>
                      <Option value="rgpd">{t('customControls.frameworks.rgpd')}</Option>
                      <Option value="sectoriel">{t('customControls.frameworks.sectoriel')}</Option>
                      <Option value="autre">{t('customControls.frameworks.autre')}</Option>
                    </Dropdown>
                  </Field>

                  <Field label={t('customControls.fields.reference')} hint={t('customControls.fields.referenceHint')}>
                    <Input
                      value={fReference}
                      onChange={(_, d) => setFReference(d.value)}
                      placeholder="ex: NIS2 Art.21(2)(d)"
                    />
                  </Field>
                </div>

                <Field label={t('customControls.fields.status')} required style={{ marginBottom: 12 }}>
                  <Dropdown
                    value={t(`customControls.status.${fStatus}`)}
                    selectedOptions={[fStatus]}
                    onOptionSelect={(_, d) => setFStatus(d.optionValue as CustomControlStatus)}
                  >
                    <Option value="pending">{t('customControls.status.pending')}</Option>
                    <Option value="conforme">{t('customControls.status.conforme')}</Option>
                    <Option value="fail">{t('customControls.status.fail')}</Option>
                  </Dropdown>
                </Field>

                <Field
                  label={t('customControls.fields.evidence')}
                  hint={t('customControls.fields.evidenceHint')}
                  style={{ marginBottom: 12 }}
                >
                  <Textarea
                    rows={3}
                    value={fEvidenceText}
                    onChange={(_, d) => setFEvidenceText(d.value)}
                  />
                </Field>

                <Field label={t('customControls.fields.attachment')} hint={t('customControls.fields.attachmentHint', { maxKB: ATTACHMENT_MAX_BYTES / 1024 })}>
                  <input
                    type="file"
                    accept="image/*,.pdf,.docx,.xlsx"
                    onChange={onAttachmentChange}
                    style={{ marginTop: 4 }}
                  />
                  {fAttachment && (
                    <div style={{
                      marginTop: 8, padding: 8, borderRadius: 6,
                      background: tokens.colorNeutralBackground3,
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                    }}>
                      <Attach20Regular />
                      <Caption1>{fAttachment.name} ({Math.round(fAttachment.size / 1024)} Ko)</Caption1>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<Delete20Regular />}
                        onClick={() => setFAttachment(undefined)}
                      />
                    </div>
                  )}
                </Field>
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={closeDialog}>
                  {t('customControls.dialog.cancel')}
                </Button>
                <Button appearance="primary" onClick={save}>
                  {creating ? t('customControls.dialog.create') : t('customControls.dialog.save')}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </div>
  );
}
